import { useState, useEffect, useCallback, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';

import {
  handleLoginCallback,
  createLoginURL,
  refreshAccessToken,
  buildLogoutUrl,
  LOGOUT_DELETE_COOKIES_QUERY_PARAM,
} from '../services/keycloak';

const REFRESH_TIMEOUT_MARGIN = 30;

const SESSION_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  ID_TOKEN: 'id_token',
  EXPIRES_AT: 'expires_at',
  REFRESH_EXPIRES_AT: 'refresh_expires_at',
  ROLES: 'roles',
  ACCOUNT_ROLES: 'accountRoles',
  PREFERRED_USERNAME: 'preferred_username',
  FAMILY_NAME: 'family_name',
} as const;

const generateCodeVerifier = () => {
  const possible =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';

  const randomValues = new Uint8Array(43);

  globalThis.crypto.getRandomValues(randomValues);

  return Array.from(randomValues)
    .map((value) => possible[value % possible.length])
    .join('');
};

const setSessionItem = (
  key: string,
  value: string | string[]
) => {
  sessionStorage.setItem(
    key,
    typeof value === 'string'
      ? value
      : JSON.stringify(value)
  );
};

const removeSessionItems = (keys: readonly string[]) => {
  keys.forEach((key) => {
    sessionStorage.removeItem(key);
  });
};

export const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] =
    useState(false);

  const [loading, setLoading] = useState(true);

  const [roles, setRoles] = useState<string[]>([]);

  const [accountRoles, setAccountRoles] =
    useState<string[]>([]);

  const hasRun = useRef(false);

  const getFullRedirectUri = useCallback(
    () => globalThis.location.origin,
    []
  );

  const clearUserSession = useCallback(() => {
    removeSessionItems([
      SESSION_KEYS.ROLES,
      SESSION_KEYS.ACCOUNT_ROLES,
      SESSION_KEYS.PREFERRED_USERNAME,
      SESSION_KEYS.FAMILY_NAME,
    ]);

    setRoles([]);
    setAccountRoles([]);
  }, []);

  const clearSession = useCallback(() => {
    removeSessionItems(Object.values(SESSION_KEYS));

    setIsAuthenticated(false);
    setRoles([]);
    setAccountRoles([]);
  }, []);

  const decodeAndSetRoles = useCallback(
    (token: string) => {
      try {
        const decoded = jwtDecode<{
          realm_access?: {
            roles?: string[];
          };
          resource_access?: {
            account?: {
              roles?: string[];
            };
          };
          preferred_username?: string;
          family_name?: string;
        }>(token);

        const extractedRoles =
          decoded.realm_access?.roles ??
          decoded.resource_access?.account?.roles ??
          [];

        const preferredUsername =
          decoded.preferred_username ?? '';

        const familyName =
          decoded.family_name ?? '';

        setSessionItem(
          SESSION_KEYS.ROLES,
          extractedRoles
        );

        setSessionItem(
          SESSION_KEYS.ACCOUNT_ROLES,
          [preferredUsername]
        );

        setSessionItem(
          SESSION_KEYS.PREFERRED_USERNAME,
          preferredUsername
        );

        setSessionItem(
          SESSION_KEYS.FAMILY_NAME,
          familyName
        );

        setRoles(extractedRoles);
        setAccountRoles([preferredUsername]);
      } catch (error) {
        console.error(
          'Failed to decode token:',
          error
        );

        clearUserSession();
      }
    },
    [clearUserSession]
  );

  const saveTokensToSession = useCallback(
    (tokens: {
      access_token: string;
      refresh_token?: string;
      id_token?: string;
      expires_in: number;
      refresh_expires_in?: number;
    }) => {
      const now = Date.now();

      setSessionItem(
        SESSION_KEYS.ACCESS_TOKEN,
        tokens.access_token
      );

      if (tokens.refresh_token) {
        setSessionItem(
          SESSION_KEYS.REFRESH_TOKEN,
          tokens.refresh_token
        );
      }

      if (tokens.id_token) {
        setSessionItem(
          SESSION_KEYS.ID_TOKEN,
          tokens.id_token
        );
      }

      setSessionItem(
        SESSION_KEYS.EXPIRES_AT,
        String(
          now + tokens.expires_in * 1000
        )
      );

      if (tokens.refresh_expires_in) {
        setSessionItem(
          SESSION_KEYS.REFRESH_EXPIRES_AT,
          String(
            now +
              tokens.refresh_expires_in * 1000
          )
        );
      }

      decodeAndSetRoles(tokens.access_token);
    },
    [decodeAndSetRoles]
  );

  const redirectToLogin = useCallback(async () => {
    const pkceCodeVerifier =
      generateCodeVerifier();

    localStorage.setItem(
      'pkceCodeVerifier',
      pkceCodeVerifier
    );

    const loginUrl = await createLoginURL(
      getFullRedirectUri(),
      pkceCodeVerifier
    );

    globalThis.location.href = loginUrl;
  }, [getFullRedirectUri]);

  const logout = useCallback(() => {
    const idToken = sessionStorage.getItem(
      SESSION_KEYS.ID_TOKEN
    );

    const logoutUrl = buildLogoutUrl(
      getFullRedirectUri(),
      idToken || undefined
    );

    clearSession();

    globalThis.location.href = logoutUrl;
  }, [clearSession, getFullRedirectUri]);

  const handleLogoutParam = useCallback(
    (logoutParam: string | null) => {
      if (!logoutParam) {
        return false;
      }

      clearSession();

      globalThis.location.href =
        globalThis.location.origin;

      return true;
    },
    [clearSession]
  );

  const handleAuthCodeFlow = useCallback(
    async (sessionExists: boolean) => {
      if (sessionExists) {
        const token = sessionStorage.getItem(
          SESSION_KEYS.ACCESS_TOKEN
        );

        if (token) {
          decodeAndSetRoles(token);
        }

        setIsAuthenticated(true);
        setLoading(false);

        return true;
      }

      const tokens =
        await handleLoginCallback();

      if (!tokens) {
        clearSession();
        await redirectToLogin();

        return false;
      }

      saveTokensToSession(tokens);

      setIsAuthenticated(true);

      globalThis.history.replaceState(
        {},
        document.title,
        globalThis.location.pathname
      );

      return true;
    },
    [
      clearSession,
      decodeAndSetRoles,
      redirectToLogin,
      saveTokensToSession,
    ]
  );

  const handleSessionFlow = useCallback(
    async () => {
      const expiresAt = sessionStorage.getItem(
        SESSION_KEYS.EXPIRES_AT
      );

      const shouldRefresh =
        expiresAt &&
        Date.now() >
          Number(expiresAt) -
            REFRESH_TIMEOUT_MARGIN * 1000;

      if (shouldRefresh) {
        try {
          const refreshToken =
            sessionStorage.getItem(
              SESSION_KEYS.REFRESH_TOKEN
            );

          if (!refreshToken) {
            throw new Error(
              'Refresh token not found'
            );
          }

          const newTokens =
            await refreshAccessToken(
              refreshToken,
              getFullRedirectUri()
            );

          saveTokensToSession(newTokens);
          setIsAuthenticated(true);
        } catch (error) {
          console.error(
            'Failed to refresh access token:',
            error
          );

          clearSession();
          await redirectToLogin();
        }

        return;
      }

      const token = sessionStorage.getItem(
        SESSION_KEYS.ACCESS_TOKEN
      );

      if (!token) {
        clearSession();
        await redirectToLogin();

        return;
      }

      decodeAndSetRoles(token);
      setIsAuthenticated(true);
    },
    [
      clearSession,
      decodeAndSetRoles,
      getFullRedirectUri,
      redirectToLogin,
      saveTokensToSession,
    ]
  );

  const processAuthFlow = useCallback(
    async () => {
      if (hasRun.current) {
        return;
      }

      hasRun.current = true;
      setLoading(true);

      try {
        const urlParams =
          new URLSearchParams(
            globalThis.location.search
          );

        const urlHasAuthCode =
          urlParams.has('code');

        const sessionExists =
          !!sessionStorage.getItem(
            SESSION_KEYS.ACCESS_TOKEN
          );

        const logoutParam =
          urlParams.get(
            LOGOUT_DELETE_COOKIES_QUERY_PARAM
          );

        if (handleLogoutParam(logoutParam)) {
          return;
        }

        if (urlHasAuthCode) {
          await handleAuthCodeFlow(
            sessionExists
          );
        } else if (sessionExists) {
          await handleSessionFlow();
        } else {
          await redirectToLogin();
        }
      } catch (error) {
        console.error(
          'Authentication error:',
          error
        );

        clearSession();
      } finally {
        setLoading(false);
      }
    },
    [
      clearSession,
      handleAuthCodeFlow,
      handleLogoutParam,
      handleSessionFlow,
      redirectToLogin,
    ]
  );

  useEffect(() => {
    processAuthFlow();
  }, [processAuthFlow]);

  return {
    isAuthenticated,
    loading,
    logout,
    roles,
    accountRoles,
  };
};